import { $, pageData } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { api } from '../lib/api.js';
import { trackDirtyForm } from '../lib/dirty-form.js';
import { toastError, toastSuccess } from '../lib/toast.js';
import { findSelectMenu } from '../lib/select-menu.js';

/**
 * The Music page.
 *
 * Three settings and a save bar. Nothing here touches what is playing: the
 * queue belongs to the voice room that hears it, and reaching into it from a
 * browser is the one thing this module refuses on Discord.
 */

interface Settings {
  voiceChannelIds: string[];
  announceChannelId: string;
  announce: boolean;
  djEnabled: boolean;
  announceCleanup: boolean;
  announceQueueEnd: boolean;
  announceSilent: boolean;
  announceButtons: boolean;
  defaultVolume: number;
  defaultLoop: string;
  autoShuffle: boolean;
  idleMinutes: number;
  stay: boolean;
  voteSkip: boolean;
  autoplay: boolean;
  lyrics: boolean;
  lyricsMode: string;
  autoRadioChannelId: string;
  autoRadioUrl: string;
}

export function initMusicPage(): void {
  const form = $<HTMLFormElement>('[data-music-form]');
  if (!form) return;

  const { settings, guildId } = pageData<{ settings: Settings; guildId: string }>();

  /*
   * The save bar first, before any widget is mounted: saving is this page's
   * essential function and must not depend on a select that can throw.
   */
  trackDirtyForm({
    form,
    successMessage: ct('settingsSaved', 'Settings saved.'),
    onSave: async () => {
      await api.put(`/api/music?guildId=${guildId}`, {
        guildId,
        djEnabled: checkbox(form, 'djEnabled'),
        djRules: readDjRules(),
        voiceChannelIds: findSelectMenu(form, 'voiceChannelIds')?.value ?? [],
        announceChannelId: findSelectMenu(form, 'announceChannelId')?.single ?? '',
        announce: checkbox(form, 'announce'),
        announceCleanup: checkbox(form, 'announceCleanup'),
        announceQueueEnd: checkbox(form, 'announceQueueEnd'),
        announceSilent: checkbox(form, 'announceSilent'),
        announceButtons: checkbox(form, 'announceButtons'),
        defaultVolume: number(form, 'defaultVolume'),
        defaultLoop: text(form, 'defaultLoop'),
        autoShuffle: checkbox(form, 'autoShuffle'),
        idleMinutes: number(form, 'idleMinutes'),
        stay: checkbox(form, 'stay'),
        voteSkip: checkbox(form, 'voteSkip'),
        autoplay: checkbox(form, 'autoplay'),
        lyrics: checkbox(form, 'lyrics'),
        lyricsMode: text(form, 'lyricsMode'),
        autoRadioChannelId: findSelectMenu(form, 'autoRadioChannelId')?.single ?? '',
        autoRadioUrl: inputValue(form, 'autoRadioUrl').trim(),
      });
    },
  });

  announceMaster(form);

  const rooms = findSelectMenu(form, 'voiceChannelIds');
  if (rooms) rooms.value = settings.voiceChannelIds;

  const announce = findSelectMenu(form, 'announceChannelId');
  if (announce) announce.value = settings.announceChannelId ? [settings.announceChannelId] : [];

  const radioRoom = findSelectMenu(form, 'autoRadioChannelId');
  if (radioRoom) radioRoom.value = settings.autoRadioChannelId ? [settings.autoRadioChannelId] : [];

  const controller = document.querySelector<HTMLSelectElement>(
    '[data-field="controllerChannelId"]',
  );
  const publish = document.querySelector<HTMLButtonElement>('[data-publish-controller]');

  publish?.addEventListener('click', () => {
    /*
     * The channel comes from the picker as it stands, not from the saved
     * settings: somebody who has just chosen a channel expects the button under
     * it to use that one, without saving first.
     */
    const channelId =
      findSelectMenu(form, 'controllerChannelId')?.single ?? controller?.value ?? '';

    if (!channelId) {
      toastError(new Error(ct('chooseChannel', 'Choose a channel first.')));
      return;
    }

    void api
      .post(`/api/music/controller?guildId=${guildId}`, { guildId, channelId })
      .then(() => {
        toastSuccess(ct('controllerPublished', 'Panel published.'));
      })
      .catch((error: unknown) => {
        toastError(error);
      });
  });
}

/**
 * The switch that governs the four settings under it.
 *
 * With it off, nothing is ever posted — not the track, not the end of the
 * queue, not the buttons — and the page went on describing all four as though
 * they applied. A server sat with announcements off, read the descriptions, and
 * concluded the module was broken.
 *
 * Dimmed rather than disabled: somebody setting the section up before switching
 * it on must still be able to fill it in.
 */
function announceMaster(form: HTMLFormElement): void {
  const master = form.querySelector<HTMLInputElement>('[data-announce-master]');
  if (!master) return;

  const notice = form.querySelector<HTMLElement>('[data-announce-off]');
  const dependents = form.querySelectorAll<HTMLElement>('[data-announce-dependent]');

  const paint = () => {
    notice?.classList.toggle('hidden', master.checked);

    for (const dependent of dependents) {
      dependent.classList.toggle('opacity-50', !master.checked);
    }
  };

  master.addEventListener('change', paint);
  paint();
}

/**
 * The permission table, read as it stands.
 *
 * Rows with nothing ticked are dropped rather than saved empty: an empty rule
 * and no rule at all mean the same thing to the bot, and keeping fifty of them
 * would make the stored settings a copy of the server's role list.
 */
function readDjRules(): { roleId: string; allowed: string[] }[] {
  const rows = document.querySelectorAll<HTMLElement>('[data-dj-row]');

  return [...rows]
    .map((row) => ({
      roleId: row.dataset.roleId ?? '',
      allowed: [...row.querySelectorAll<HTMLInputElement>('[data-dj-action]')]
        .filter((box) => box.checked)
        .map((box) => box.dataset.djAction ?? ''),
    }))
    .filter((rule) => rule.roleId && rule.allowed.length > 0);
}

function number(form: HTMLFormElement, name: string): number {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement ? Number(field.value) || 0 : 0;
}

/** A plain `<select>`, left native: three fixed choices need no component. */
function inputValue(form: HTMLFormElement, name: string): string {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement ? field.value : '';
}

function text(form: HTMLFormElement, name: string): string {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLSelectElement ? field.value : '';
}

function checkbox(form: HTMLFormElement, name: string): boolean {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement ? field.checked : false;
}

/* ── The web player ──────────────────────────────────────────────────────── */

interface NowPlaying {
  playing: boolean;
  current: number;
  volume: number;
  loop: string;
  paused: boolean;
  tracks: { title: string; author: string; duration: number }[];
}

/** Slow enough to be free, quick enough that a skip is visible. */
const POLL_MS = 5_000;

/**
 * What the room is listening to, drawn from the server every few seconds.
 *
 * Polling and not a socket: this list moves once every three minutes, and a
 * connection held open for that would cost more than it carries. The interval
 * stops when the page is hidden — a dashboard left open in a background tab
 * should not ask a question every five seconds all night.
 */
export function initMusicPlayer(): void {
  const panel = $<HTMLElement>('[data-web-player]');
  if (!panel) return;

  const guildId = new URLSearchParams(window.location.search).get('guildId') ?? '';
  if (!guildId) return;

  const current = panel.querySelector<HTMLElement>('[data-player-current]');
  const author = panel.querySelector<HTMLElement>('[data-player-author]');
  const queue = panel.querySelector<HTMLElement>('[data-player-queue]');
  const volume = panel.querySelector<HTMLInputElement>('[data-player-volume]');
  const volumeLabel = panel.querySelector<HTMLElement>('[data-player-volume-label]');
  const stateLabel = panel.querySelector<HTMLElement>('[data-player-state-label]');
  const state = panel.querySelector<HTMLElement>('[data-player-state]');

  const emptyRow = () => {
    const item = document.createElement('li');
    item.className = 'text-faint';
    item.textContent = ct('queueEmpty', 'Nothing else queued.');
    return item;
  };

  const draw = (status: NowPlaying) => {
    const playing = status.playing ? status.tracks[status.current] : null;

    if (current)
      current.textContent = playing ? playing.title : ct('playerIdle', 'Nothing playing.');
    if (author) author.textContent = playing?.author ?? '';

    /*
     * The badge says which of three states the room is in. "Paused" and
     * "nothing playing" look identical on a silent player, and telling them
     * apart is the whole reason the badge is there.
     */
    if (stateLabel) {
      stateLabel.textContent = !playing
        ? ct('playerIdle', 'Nothing playing.')
        : status.paused
          ? ct('playerPaused', 'Paused')
          : ct('playerPlaying', 'Playing');
    }

    state?.classList.toggle('badge-success', Boolean(playing) && !status.paused);
    state?.classList.toggle('badge-ghost', !playing || status.paused);

    if (queue) {
      const upcoming = status.tracks.slice(status.current + 1);

      queue.replaceChildren(
        ...(upcoming.length > 0
          ? upcoming.map((track, index) => {
              const item = document.createElement('li');
              item.className = 'flex gap-2';

              const rank = document.createElement('span');
              rank.className = 'text-faint w-5 tabular-nums';
              rank.textContent = String(index + 1);

              const title = document.createElement('span');
              title.className = 'truncate';
              title.textContent = track.title;

              item.append(rank, title);
              return item;
            })
          : [emptyRow()]),
      );
    }

    // Only when nobody is dragging it: writing into the slider under a finger
    // is how a volume control fights its user.
    if (volume && document.activeElement !== volume) volume.value = String(status.volume);
    if (volumeLabel) volumeLabel.textContent = `${String(status.volume)} %`;
  };

  const refresh = () =>
    api
      .get<NowPlaying>(`/api/music/now?guildId=${guildId}`)
      .then(draw)
      .catch(() => {
        // A page that loses its player must not lose its settings form.
      });

  void refresh();

  let timer = window.setInterval(() => {
    if (!document.hidden) void refresh();
  }, POLL_MS);

  window.addEventListener('pagehide', () => {
    window.clearInterval(timer);
    timer = 0;
  });

  const send = (action: string, value?: number) =>
    api
      .post(`/api/music/control?guildId=${guildId}`, { guildId, action, value })
      .then(() => refresh())
      .catch((error: unknown) => {
        toastError(error);
      });

  for (const button of panel.querySelectorAll<HTMLButtonElement>('[data-player-action]')) {
    button.addEventListener('click', () => {
      void send(button.dataset.playerAction ?? '');
    });
  }

  volume?.addEventListener('input', () => {
    // The label follows the finger; the server is told when it stops moving.
    if (volumeLabel) volumeLabel.textContent = `${volume.value} %`;
  });

  volume?.addEventListener('change', () => {
    void send('volume', Number(volume.value));
  });
}
